import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, Users, Trophy, Handshake, Info, ShieldAlert, Plus, Lock, Calendar } from 'lucide-react';
import { db, getCollectionPath } from '../../lib/firebase';
import { addDoc, collection, serverTimestamp, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { ClubSelectionModal } from './ClubSelectionModal';
import { useBackButton } from '../../hooks/useBackButton';

const getLocalDateStr = (d: Date = new Date()) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

interface BookingData {
  id: string;
  clubId: string;
  clubName: string;
  date: string;
  time: string;
  endTime: string;
  courtId: string;
  courtName: string;
  totalPrice?: number;
}

interface PublicMatchCreatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (matchId: string) => void;
  booking?: BookingData | null;
  currentUser: { uid: string; isProfileValidated?: boolean; category?: string; gender?: string };
  sedesPublicas?: any[];
  config?: any;
}

export function PublicMatchCreatorModal({ isOpen, onClose, onCreated, booking, currentUser, sedesPublicas, config }: PublicMatchCreatorModalProps) {
  useBackButton(isOpen, onClose);
  
  const [title, setTitle] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>(['Todas']);
  const [gender, setGender] = useState<'Masculino' | 'Femenino' | 'Mixto' | 'Libre'>('Masculino');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedClubId, setSelectedClubId] = useState('indeterminado');
  const [selectedClubName, setSelectedClubName] = useState('Club a confirmar');
  const [selectedDate, setSelectedDate] = useState(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return getLocalDateStr(tomorrow);
  });
  const [selectedTime, setSelectedTime] = useState('19:00');
  const [showClubSelection, setShowClubSelection] = useState(false);

  const calculateEndTime = (timeStr: string): string => {
    try {
      const [h, m] = timeStr.split(':').map(Number);
      const totalMin = h * 60 + m + 90; // Default 90 minutes
      const endH = Math.floor(totalMin / 60) % 24;
      const endM = totalMin % 60;
      return `${endH.toString().padStart(2, '0')}:${endM.toString().padStart(2, '0')}`;
    } catch {
      return '';
    }
  };

  const availableCategories = ['Todas', '1ra', '2da', '3ra', '4ta', '5ta', '6ta', '7ma', '8va'];

  const toggleCategory = (cat: string) => {
    if (cat === 'Todas') {
      setSelectedCategories(['Todas']);
      return;
    }
    setSelectedCategories(prev => {
      const filtered = prev.filter(c => c !== 'Todas');
      if (filtered.includes(cat)) {
        const newCats = filtered.filter(c => c !== cat);
        return newCats.length === 0 ? ['Todas'] : newCats;
      } else {
        return [...filtered, cat];
      }
    });
  };

  const handleCreateClick = () => {
    setShowClubSelection(true);
  };

  const executeCreation = async (overrideClubId?: string, overrideClubName?: string) => {
    setIsSubmitting(true);
    try {
      const matchData = {
        creatorId: currentUser.uid,
        clubId: overrideClubId || selectedClubId,
        clubName: overrideClubName || selectedClubName,
        reservationId: '',
        date: selectedDate,
        time: selectedTime,
        endTime: calculateEndTime(selectedTime),
        courtId: '',
        courtName: 'A confirmar',
        matchType: '2v2',
        mode: 'amistoso',
        categories: selectedCategories.includes('Todas') ? ['Libre'] : selectedCategories,
        gender,
        totalPrice: 0,
        pricePerPlayer: 0,
        players: [currentUser.uid],
        slotMapping: { "0": currentUser.uid },
        status: 'open',
        createdAt: serverTimestamp(),
        title: title.trim() || ''
      };

      const docRef = await addDoc(collection(db, getCollectionPath('public_matches')), matchData);
      
      if (onCreated) {
        onCreated(docRef.id);
      } else {
        onClose();
      }
    } catch (err) {
      console.error("Error creating public match:", err);
      alert("Hubo un error al publicar el partido.");
    } finally {
      setIsSubmitting(false);
      setShowClubSelection(false);
    }
  };

  if (typeof document === 'undefined') return null;

  const primaryColor = config?.primaryColor || '#eab308';

  return createPortal(
    <>
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          transition={{ type: 'spring', duration: 0.35, bounce: 0.15 }}
          className="bg-[#1e2538] w-[92%] sm:w-full sm:max-w-md rounded-2xl sm:rounded-3xl border border-white/15 overflow-hidden flex flex-col max-h-[82dvh] sm:max-h-[85vh] shadow-[0_25px_60px_rgba(0,0,0,0.9)] relative"
        >
          {/* Header */}
          <div className="py-2.5 px-3.5 sm:py-3.5 sm:px-5 border-b border-white/10 flex items-center justify-center bg-black/25 sticky top-0 z-10 shrink-0 relative">
            <div className="flex items-center gap-2">
              <div 
                style={{ backgroundColor: `${primaryColor}15`, borderColor: `${primaryColor}30`, boxShadow: `0 0 10px ${primaryColor}20` }}
                className="w-7.5 h-7.5 rounded-full flex items-center justify-center border shrink-0"
              >
                <Plus size={14} style={{ color: primaryColor }} />
              </div>
              <h3 className="text-sm sm:text-base font-black uppercase tracking-wider text-white leading-tight">Publicar Partido</h3>
            </div>
            <button
              onClick={onClose}
              className="absolute right-3.5 sm:right-5 p-1 hover:bg-white/10 rounded-full transition-colors text-white/50 hover:text-white cursor-pointer"
            >
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div className="p-3.5 sm:p-5 overflow-y-auto flex flex-col gap-3.5 sm:gap-4.5 relative z-10 custom-scrollbar">
            {/* Título del Partido */}
            <div>
              <label className="text-[9px] sm:text-[10px] font-black text-white/70 uppercase tracking-widest mb-1 block text-center">
                Título del Partido (opcional)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: Partido de padel + asado de los Viernes"
                style={{
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = primaryColor;
                  e.currentTarget.style.boxShadow = `0 0 10px ${primaryColor}20`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                className="w-full bg-white/5 border rounded-xl px-3 py-2 text-xs sm:text-sm text-white focus:outline-none transition-all placeholder:text-white/30 text-center"
                maxLength={40}
              />
            </div>

            {/* Fecha y Hora */}
            <div className="space-y-3 shrink-0 text-xs">
              <div className="grid grid-cols-2 gap-2">
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-white/50 uppercase tracking-widest font-bold">Fecha estimada</label>
                  <input
                    type="date"
                    value={selectedDate}
                    min={getLocalDateStr()}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    style={{ colorScheme: 'dark' }}
                    className="w-full bg-white/10 border border-white/10 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-amber-500 transition-colors text-center"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-[9px] text-white/50 uppercase tracking-widest font-bold">Horario estimado</label>
                  <input
                    type="time"
                    value={selectedTime}
                    onChange={(e) => setSelectedTime(e.target.value)}
                    style={{ colorScheme: 'dark' }}
                    className="w-full bg-white/10 border border-white/10 rounded-lg p-2 text-white text-xs focus:outline-none focus:border-amber-500 transition-colors text-center"
                  />
                </div>
              </div>
            </div>

            {/* Categorías (Nivel buscado) */}
            <div className="space-y-1 text-center">
              <label className="text-[9px] sm:text-[10px] font-black text-white/70 uppercase tracking-widest mb-1 block">
                Nivel Buscado (Categorías)
              </label>

              <div className="flex flex-col gap-1.5 w-full px-1">
                <div className="flex justify-center gap-1 sm:gap-1.5 w-full">
                  {availableCategories.slice(0, 5).map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className={`flex-1 min-w-0 px-1 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold border transition-all cursor-pointer truncate ${
                        selectedCategories.includes(cat)
                          ? cat === 'Todas' 
                            ? 'bg-white text-black font-black border-white shadow-[0_0_8px_rgba(255,255,255,0.3)] scale-[1.02]' 
                            : 'bg-yellow-400 text-black font-black border-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.3)] scale-[1.02]'
                          : 'bg-white/10 border-white/10 text-white/75 hover:bg-white/15 hover:text-white'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
                <div className="flex justify-center gap-1 sm:gap-1.5 w-[80%] mx-auto">
                  {availableCategories.slice(5).map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => toggleCategory(cat)}
                      className={`flex-1 min-w-0 px-1 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold border transition-all cursor-pointer truncate ${
                        selectedCategories.includes(cat)
                          ? cat === 'Todas' 
                            ? 'bg-white text-black font-black border-white shadow-[0_0_8px_rgba(255,255,255,0.3)] scale-[1.02]' 
                            : 'bg-yellow-400 text-black font-black border-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.3)] scale-[1.02]'
                          : 'bg-white/10 border-white/10 text-white/75 hover:bg-white/15 hover:text-white'
                      }`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Género */}
            <div>
              <label className="text-[9px] sm:text-[10px] font-black text-white/70 uppercase tracking-widest mb-1 block text-center">
                Género
              </label>
              <div className="grid grid-cols-4 gap-1.5">
                {['Masculino', 'Femenino', 'Mixto', 'Libre'].map(g => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setGender(g as any)}
                    className={`p-1 sm:p-2 rounded-xl text-[10px] sm:text-xs font-bold border transition-all cursor-pointer flex items-center justify-center ${
                      gender === g 
                        ? 'bg-white text-black font-black border-white shadow-[0_0_8px_rgba(255,255,255,0.3)] scale-[1.02]' 
                        : 'bg-white/10 border-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

          </div>

          {/* Footer Action */}
          <div className="p-3 sm:p-4 border-t border-white/10 bg-black/25 mt-auto relative z-10 shrink-0">
            <button
              onClick={handleCreateClick}
              disabled={isSubmitting}
              style={!isSubmitting ? { backgroundColor: primaryColor, color: '#000000', boxShadow: `0 4px 20px ${primaryColor}35` } : {}}
              className="w-full font-black uppercase tracking-widest py-2.5 sm:py-3 text-xs sm:text-sm rounded-xl hover:opacity-90 active:scale-98 transition-all disabled:bg-zinc-800 disabled:text-zinc-500 disabled:border-zinc-700 disabled:active:scale-100 disabled:cursor-not-allowed flex justify-center items-center gap-2 cursor-pointer border border-transparent"
            >
              {isSubmitting ? (
                <div className="w-4 h-4 border-2 border-black/20 border-t-black rounded-full animate-spin" />
              ) : (
                'Publicar Anuncio'
              )}
            </button>
          </div>
        </motion.div>
        </div>
      )}
    </AnimatePresence>
    <ClubSelectionModal 
      isOpen={showClubSelection}
      onClose={() => setShowClubSelection(false)}
      onConfirm={(clubId, clubName) => {
        executeCreation(clubId, clubName);
      }}
      clubs={sedesPublicas || []}
      primaryColor={primaryColor}
    />
    </>,
    document.body
  );
}
